import React, {memo, useCallback} from 'react';
import {get} from 'lodash';
import {FeatureFlag} from 'react-launch-darkly';

const NOT_ASSIGNED_LABEL = 'N/A';

const AvgEngagementOptions = {
    Any: 'ANY',
    Eng_1K: 'ENG_1K',
    Eng_1M: 'ENG_1M',
    Eng_5K: 'ENG_5K',
    Eng_10K: 'ENG_10K',
    Eng_50K: 'ENG_50K',
    Eng_100: 'ENG_100',
    Eng_100K: 'ENG_100K',
    Eng_200: 'ENG_200',
    Eng_300: 'ENG_300',
    Eng_400: 'ENG_400',
    Eng_500: 'ENG_500',
    Eng_500K: 'ENG_500K'
};

const AvgEngagementsLabels = {
    [AvgEngagementOptions.Any]: 'Any',
    [AvgEngagementOptions.Eng_100]: '100',
    [AvgEngagementOptions.Eng_200]: '200',
    [AvgEngagementOptions.Eng_300]: '300',
    [AvgEngagementOptions.Eng_400]: '400',
    [AvgEngagementOptions.Eng_500]: '500',
    [AvgEngagementOptions.Eng_1K]: '1k',
    [AvgEngagementOptions.Eng_5K]: '5k',
    [AvgEngagementOptions.Eng_10K]: '10k',
    [AvgEngagementOptions.Eng_50K]: '50k',
    [AvgEngagementOptions.Eng_100K]: '100k',
    [AvgEngagementOptions.Eng_500K]: '500k',
    [AvgEngagementOptions.Eng_1M]: '>1 Million'
}

const TargetingDetails = memo(({details, campaignType}) => {
    const genderValue = get(details, 'gender') || NOT_ASSIGNED_LABEL;
    const minAgeValue = get(details, 'age.minAge');
    const maxAgeValue = get(details, 'age.maxAge');
    const minFollowersValue = get(details, 'followers.minFollowers');
    const maxFollowersValue = get(details, 'followers.maxFollowers');
    const followersTiers = get(details, 'followersTiers');
    const locations = get(details, 'locations') || [];
    const languages = get(details, 'languages') || [];
    const categories = get(details, 'categories') || [];
    const avgEngagements = get(details, 'avgEngagements') || NOT_ASSIGNED_LABEL;
    const engagementRate = get(details, 'engagementRate') || NOT_ASSIGNED_LABEL;

    const renderAgeValue = useCallback(() => {
        if (minAgeValue && maxAgeValue) {
            return `From ${minAgeValue}, To ${maxAgeValue}`;
        }
        if (minAgeValue) {
            return `From ${minAgeValue}`;
        }
        if (maxAgeValue) {
            return `To ${maxAgeValue}`;
        }

        return NOT_ASSIGNED_LABEL;
    }, [minAgeValue, maxAgeValue]);

    const renderFollowerTiersValue = useCallback(() => {
        if (followersTiers && followersTiers.length) {
            return (
                <>
                    {followersTiers.map(({label, description}, index) => (
                        <span key={index}>
                            {`${label} ${description}`}{index < followersTiers.length - 1 ? ', ' : ''}
                        </span>
                ))}
                </>
            );
        }

        return NOT_ASSIGNED_LABEL;
    }, [followersTiers]);

    const renderFollowerValue = useCallback(() => {
        if (minFollowersValue && maxFollowersValue) {
            return `From ${minFollowersValue}, To ${maxFollowersValue}`;
        }
        if (minFollowersValue) {
            return `From ${minFollowersValue}`;
        }
        if (maxFollowersValue) {
            return `To ${maxFollowersValue}`;
        }

        return NOT_ASSIGNED_LABEL;
    }, [minFollowersValue, maxFollowersValue]);

    const renderLocationValue = useCallback(() => {
        if (locations.length) {
            return locations.map(({name}) => name).join(',');
        }

        return NOT_ASSIGNED_LABEL;
    }, [locations]);

    const renderLanguageValue = useCallback(() => {
        if (languages.length) {
            return languages.map(({name}) => name).join(',');
        }

        return NOT_ASSIGNED_LABEL;
    }, [languages]);

    const renderCategoryValue = useCallback(() => {
        if (categories.length) {
            return (
                <>
                    {categories.map(category => (
                        <ul key={category.primaryCategoryName} className="list-unstyled">
                            <li>
                                {category.primaryCategoryName}
                                {category.subCategories && (
                                    <ul>
                                        {category.subCategories?.map(({subCategoryName}) => (
                                            <li key={subCategoryName}>{subCategoryName}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        </ul>
                    ))}
                </>
            );
        }

        return NOT_ASSIGNED_LABEL;
    }, [categories]);

    const getAverageEngagementsLabel = useCallback(() => {
        if (campaignType === 'instagram') {
            return 'Avg. Reels Plays';
        }

        if (campaignType === 'tiktok') {
            return 'Avg. Views';
        }

        return 'Avg. Engagements';
    }, [campaignType]);

    const getAverageEngagementsValue = useCallback(() => {
        const engagementsLabel = AvgEngagementsLabels[avgEngagements];
        return engagementsLabel || NOT_ASSIGNED_LABEL;
    }, [avgEngagements]);

    return (
        <div className="detail-section">
            <div className="detail-section-title">
                <h5>Targeting</h5>
            </div>

            <div className="detail-section-grid">
                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Tiers</p>
                    <div className="item-value">
                        <FeatureFlag
                      flagKey="budget-v2-enabled"
                      renderFeatureCallback={flag =>
                          flag
                              ? renderFollowerTiersValue()
                              : renderFollowerValue()
                      }
                      renderDefaultCallback={renderFollowerValue}
                  />
                    </div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Categories</p>
                    <div className="item-value">{renderCategoryValue()}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Gender</p>
                    <div className="item-value">{genderValue}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Age</p>
                    <div className="item-value">{renderAgeValue()}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Location</p>
                    <div className="item-value">{renderLocationValue()}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Engagement Rate Min.</p>
                    <div className="item-value">{engagementRate}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">
                        {getAverageEngagementsLabel()}
                    </p>
                    <div className="item-value">{getAverageEngagementsValue()}</div>
                </div>

                <div className="detail-section-grid-item">
                    <p className="content-section-title -no-margin">Language</p>
                    <div className="item-value">{renderLanguageValue()}</div>
                </div>
            </div>
        </div>
    );
});

TargetingDetails.displayName = 'TargetingDetails';

export default TargetingDetails;



// WEBPACK FOOTER //
// ./components/verify_receipt_common/targeting_details.js